import type { CategoryId, Deadline, JurisdictionId, RegulatoryRecord } from '../../data/regulations/types'
import { addDaysIso, compareIso, inDateRange, todayIsoInUae } from './dates'

export type DeadlineView = Deadline & {
  recordId: string
  slug: string
  title: string
  shortTitle: string
  authority: string
  jurisdiction: JurisdictionId[]
  categories: CategoryId[]
  status: RegulatoryRecord['status']
}

export type DeadlineQuery = {
  window?: '30' | '90' | '365' | 'custom' | 'all'
  start?: string
  end?: string
  authority?: string
  jurisdictions?: JurisdictionId[]
  categories?: CategoryId[]
  recordIds?: string[]
  confirmedOnly?: boolean
  today?: string
}

export function flattenDeadlines(records: RegulatoryRecord[]): DeadlineView[] {
  const views: DeadlineView[] = []
  for (const record of records) {
    for (const deadline of record.deadlines) {
      views.push({
        ...deadline,
        recordId: record.id,
        slug: record.slug,
        title: record.title,
        shortTitle: record.shortTitle,
        authority: record.authority,
        jurisdiction: record.jurisdiction,
        categories: record.categories,
        status: record.status,
      })
    }
  }
  views.sort((a, b) => compareIso(a.date, b.date) || a.title.localeCompare(b.title))
  return views
}

export function filterDeadlines(records: RegulatoryRecord[], query: DeadlineQuery = {}): { dated: DeadlineView[]; undated: DeadlineView[] } {
  const today = query.today || todayIsoInUae()
  let start = query.start
  let end = query.end
  if (query.window === '30') {
    start = today
    end = addDaysIso(today, 30) || undefined
  } else if (query.window === '90') {
    start = today
    end = addDaysIso(today, 90) || undefined
  } else if (query.window === '365') {
    start = today
    end = addDaysIso(today, 365) || undefined
  }

  const matched = flattenDeadlines(records).filter((item) => {
    if (query.authority && !item.authority.toLowerCase().includes(query.authority.toLowerCase())) return false
    if (query.jurisdictions?.length && !query.jurisdictions.some((id) => item.jurisdiction.includes(id))) return false
    if (query.categories?.length && !query.categories.some((id) => item.categories.includes(id))) return false
    if (query.recordIds && !query.recordIds.includes(item.recordId)) return false
    if (query.confirmedOnly && item.certainty !== 'confirmed') return false
    return true
  })

  const undated = matched.filter((item) => !item.date)
  const windowed = query.window && query.window !== 'all'
  const dated = matched.filter((item) => {
    if (!item.date) return false
    if (!windowed && !query.start && !query.end) return true
    return inDateRange(item.date, start, end)
  })
  return { dated, undated: windowed || query.confirmedOnly ? undated : undated }
}
